import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Radio,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import Phone1 from '@forwardflow/assets/onboarding/phone1.png'
import Phone2 from '@forwardflow/assets/onboarding/phone2.png'
import { useState } from 'react'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import { CheckCircle02 } from '@forwardflow/icons/CheckCircle02'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'

const PhoneImage = ({
   src,
   alt,
   number,
   caller,
}: {
   src: string
   alt: string
   number: string
   caller: string
}) => {
   return (
      <Group className="w-72 relative">
         <Stack
            className="absolute top-24 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full"
            gap={2}
            justify="center"
            align="center"
         >
            <p className="text-white font-medium text-xs">Incoming Call...</p>
            <p className="text-white font-semibold text-lg">{number}</p>
            <p className="text-white/60 font-medium text-xs">{caller}</p>
         </Stack>
         <img src={src} alt={alt} className="w-72" />
      </Group>
   )
}

const BENEFITS = [
   'Builds Trust',
   'Gives a Proffesional Image',
   'Higher Answer Rates',
]

export const CnameSetup = () => {
   const { getNextStep, getPreviousStep, onboardingData, setBusinessDetails } =
      useOnboardingStoreV2()
   const { businessDetails } = onboardingData
   const [cname, setCname] = useState(businessDetails?.businessCname || '')
   const [skipCname, setSkipCname] = useState(false)
   const [confirmCname, setConfirmCname] = useState(Boolean(businessDetails?.businessCname))

   return (
      <Stack>
         <ContentCard className="space-y-8">
            <Stack gap={2}>
               <p className="text-white font-medium text-lg">
                  Show your business name on Caller ID (CNAM)
               </p>
               <p className="text-[#99A1AF] font-medium">
                  CNAM displays Caller ID - bypassing auto-call forward features
                  and directly improving answer rates.
               </p>
            </Stack>
            <Grid>
               <Grid.Col span={6} className="flex justify-start">
                  <Stack className="w-72 items-center" gap={8}>
                     <p className="text-white font-medium text-sm">
                        Without CNAM
                     </p>
                     <PhoneImage
                        src={Phone1}
                        alt="Phone 1"
                        number="(405) 555-0145"
                        caller="Unknown Number"
                     />
                  </Stack>
               </Grid.Col>
               <Grid.Col span={6} className="flex justify-end">
                  <Stack className="w-72 items-center" gap={8}>
                     <p className="text-white font-medium text-sm">With CNAM</p>
                     <PhoneImage
                        src={Phone2}
                        alt="Phone 2"
                        number={cname || 'FORWARD FLOW'}
                        caller="(405) 555-0145"
                     />
                  </Stack>
               </Grid.Col>
            </Grid>
            <Group justify="space-between">
               {BENEFITS.map((benefit) => (
                  <Group key={benefit} gap={12}>
                     <CheckCircle02 width={16} color="#00D492" />
                     <p className="text-white font-medium">{benefit}</p>
                  </Group>
               ))}
            </Group>
         </ContentCard>
         <ContentCard className="space-y-6">
            <button className="w-full text-left" onClick={() => setSkipCname(false)}>
               <Stack className="rounded-[8px] border border-white/10 bg-white/5 p-6">
                  <Group justify="space-between" className="cursor-pointer" onClick={() => setSkipCname(false)}>
                     <Group>
                        <Radio
                           size="xs"
                           checked={!skipCname}
                           readOnly
                        />
                        <p>Yes, Register a CNAM for my outbound calls</p>
                     </Group>
                     <p className="rounded-[4px] bg-[#A684FF]/50 text-white font-medium text-xs px-2 py-1">
                        RECOMMENDED
                     </p>
                  </Group>
                  {!skipCname && <Stack onClick={(e) => e.stopPropagation()}>
                     <TextInput
                        description="Only letters, numbers, space, period, comma are allowed. ID Name must start with a letter"
                        value={cname}
                        label="Caller ID Name"
                        placeholder="eg: FORWARD FLOW"
                        rightSectionWidth={50}
                        rightSection={
                           <span className="text-white/60 font-medium text-xs">
                              {cname.length || 0}/15
                           </span>
                        }
                        onChange={(e) => {
                           const value = e.target.value
                           // Only allow letters, numbers, space, period, comma, must start with letter, and max length 15
                           if (
                              (/^[a-zA-Z][a-zA-Z0-9\s.,]*$/.test(value) &&
                                 value.length <= 15) ||
                              value === ''
                           ) {
                              setCname(value)
                           }
                        }}
                        error={cname.length > 15}
                     />
                     <p className="border border-[#A684FF]/30 rounded-[5px] bg-[rgba(166,132,255,0.05)] p-2 px-4">
                        ** adds $ 0.0075 to each phone call
                     </p>
                     <Group gap={8}>
                        <Checkbox
                           size="xs"
                           checked={confirmCname}
                           onChange={() => setConfirmCname(!confirmCname)}
                           disabled={!cname}
                        />
                        <p className="text-white/60 font-medium text-sm"></p>I confirm
                        this CNAM accurately represents my business/DBA
                     </Group>
                  </Stack>}
               </Stack>
            </button>
            <button className="w-full text-left" onClick={() => {
               setSkipCname(true)
               setConfirmCname(false)
               setCname('')
            }}>
               <Stack className="rounded-[8px] border border-white/10 bg-white/5 p-6">
                  <Group >
                     <Radio
                        size="xs"
                        checked={skipCname}
                        readOnly
                     />
                     <p>No, Skip for now</p>
                  </Group>
                  <p className="text-white/60 font-medium text-sm">Calls may show as an unknown number, are more likely to be sent to voicemail, and can hurt answer rates and trust. You can always add CNAM later in Settings</p>
               </Stack>
            </button>
         </ContentCard>
         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     disabled={!skipCname && (!cname || !confirmCname)}
                     onClick={() => {
                        setBusinessDetails({
                           ...businessDetails,
                           businessCname: cname,
                        })
                        getNextStep()
                     }}
                  >
                     Save & Continue <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
